import { darken, lighten, rem } from "polished"

const global_font_size_px = 16

const _base_pixel_sizes = Array.from(
  new Set([
    2,
    4,
    5,
    6,
    8,
    10,
    12,
    14,
    16,
    18,
    20,
    24,
    28,
    32,
    36,
    48,
    60,
    80,
    96,
    100,
    128,
    192,
    ...[global_font_size_px],
  ]),
)

const size_pix_to_rem = {}
_base_pixel_sizes.forEach((size) => (size_pix_to_rem[size] = rem(size, global_font_size_px)))

const colors = {
  white: "#ffffff",
  darkBlue: "#1d3557",
  darkBlue80: "#1d3557cc", // 80% opacity
  darkBlue50: "#1d355780", // 50% opacity
  darkBlue20: "#1d355733", // 20% opacity
  blue: "#457b9d",
  sky: "#a8dadc",
  sky80: "#a8dadccc", // 80% opacity
  sky50: "#a8dadc80", // 50% opacity
  lightGrey: "#ededed",
  middleGrey: "#D9D9D9",
  grey: "#999",
  red: "#e30613",
  orange: "#ff8e4f",
  yellow: "#e9c46a",
  green: "#2a9d8f",
  magenta: "#d946ef",

  // non design colors
  defaultRed: "red",
  defaultGreen: "green",
  defaultYellow: "yellow",
}

const variants = Object.freeze({
  brand: "brand",
  standard: "standard",
  content: "content",
  header: "header",
  highlight: "highlight",
  alert: "alert",
  success: "success",
  selectOn: "selectOn",
  selectOff: "selectOff",
  primary: "primary",
  secondary: "secondary",
  ghost: "ghost",
  danger: "danger",
  warning: "warning",
  disabled: "disabled",
  dimmed: "dimmed",
  modal: "modal",
  readOnly: "readOnly",
  accent: "accent",
})

const theme = {
  screen: {
    minWidth: 640,
    minHeight: 200,
  },
  spaces: {
    tiny: size_pix_to_rem[2],
    extraSmall: size_pix_to_rem[4],
    xxs: size_pix_to_rem[6],
    xs: size_pix_to_rem[8],
    xtrasmall: size_pix_to_rem[10],
    small: size_pix_to_rem[12],
    smallToNormal: size_pix_to_rem[14],
    normal: size_pix_to_rem[16],
    normalToMedium: size_pix_to_rem[18],
    medium: size_pix_to_rem[20],
    large: size_pix_to_rem[24],
    largeToBig: size_pix_to_rem[32],
    big: size_pix_to_rem[48],
    bigToHuge: size_pix_to_rem[60],
    huge: size_pix_to_rem[96],
  },
  sizes: {
    font: "1rem",
    xtrasmall: size_pix_to_rem[18],
    small: size_pix_to_rem[24],
    normal: size_pix_to_rem[32],
    medium: size_pix_to_rem[48],
    mediumToLarge: size_pix_to_rem[60],
    large: size_pix_to_rem[80],
    big: size_pix_to_rem[128],
    huge: size_pix_to_rem[192],
  },
  iconSizes: {
    small: size_pix_to_rem[18],
    normal: size_pix_to_rem[24],
    large: size_pix_to_rem[32],
    xtraLarge: size_pix_to_rem[36],
  },
  fonts: {
    weight: {
      light: "300",
      regular: "400",
      bold: "700",
    },
    size: {
      // TODO: reduce the values to those that are needed - EINZA-836

      root: global_font_size_px + "px",
      system: "1rem",
      small: "0.75rem",
      normal: "1rem",
      medium: "1.25rem",

      // used for paragraph and button
      xtraLargeParagraph: size_pix_to_rem[36],
      large: size_pix_to_rem[24],
      paragraph: size_pix_to_rem[18],
      smallParagraph: size_pix_to_rem[14],
      xtraSmallParagraph: size_pix_to_rem[12],
      // -----------------------------

      button: size_pix_to_rem[18],

      input: size_pix_to_rem[18],
      label: size_pix_to_rem[14],

      heading1: size_pix_to_rem[48],
      heading2: size_pix_to_rem[36],
      heading3: size_pix_to_rem[28],
      heading4: size_pix_to_rem[24],
      heading5: size_pix_to_rem[18],
      heading6: size_pix_to_rem[14],
    },
    lineHeight: {
      quater: 0.25,
      half: 0.5,
      halfNSpace: 0.75,
      single: 1,
      singleNSpace: 1.25,
      singleNThird: 1.33,
      oneAndHalf: 1.5,
      double: 2,
    },

    family: { primary: `--font-roboto-condensed` },
  },
  colors: {
    _base: { ...colors },

    bg: colors.lightGrey,
    fg: colors.darkBlue,

    [variants.brand]: {
      fg: colors.white,
      bg: colors.darkBlue,
      border: darken(0.1, colors.middleGrey),
    },
    [variants.standard]: {
      fg: colors.darkBlue,
      bg: colors.lightGrey,
      border: darken(0.1, colors.middleGrey),
    },
    [variants.content]: {
      fg: colors.darkBlue,
      bg: colors.white,
      border: colors.middleGrey,
    },
    [variants.header]: {
      fg: colors.darkBlue,
      bg: colors.lightGrey,
      border: colors.middleGrey,
    },
    [variants.highlight]: {
      fg: colors.darkBlue,
      bg: colors.white,
      border: lighten(0.3, colors.darkBlue),
    },
    [variants.alert]: {
      fg: colors.white,
      bg: colors.yellow,
      border: darken(0.1, colors.defaultRed),
    },
    [variants.success]: {
      fg: colors.white,
      bg: colors.green,
      border: darken(0.1, colors.green),
    },
    [variants.selectOn]: {
      fg: colors.darkBlue,
      bg: colors.middleGrey,
      border: darken(0.1, colors.darkBlue),
    },
    [variants.selectOff]: {
      fg: colors.white,
      bg: colors.darkBlue,
      border: darken(0.1, colors.darkBlue),
    },
    [variants.disabled]: {
      fg: colors.darkBlue50,
      bg: colors.lightGrey,
      border: colors.lightGrey,
    },
    [variants.readOnly]: {
      fg: colors.darkBlue,
      bg: "transparent",
      border: "transparent",
    },
    [variants.dimmed]: {
      fg: colors.white,
      bg: colors.grey,
      border: darken(0.3, colors.middleGrey),
    },
    [variants.primary]: {
      fg: colors.white,
      bg: colors.darkBlue,
      border: colors.darkBlue,
    },
    [variants.secondary]: {
      fg: colors.darkBlue,
      bg: colors.sky,
      border: colors.sky,
    },
    [variants.ghost]: {
      fg: colors.darkBlue,
      bg: colors.lightGrey,
      border: colors.lightGrey,
    },
    [variants.danger]: {
      fg: colors.white,
      bg: colors.red,
      border: darken(0.1, colors.red),
    },
    [variants.warning]: {
      fg: colors.white,
      bg: colors.orange,
      border: darken(0.1, colors.defaultYellow),
    },
    [variants.modal]: {
      fg: colors.darkBlue,
      bg: colors.darkBlue50,
      border: darken(0.1, colors.middleGrey),
    },
    [variants.accent]: {
      fg: colors.white,
      bg: colors.magenta,
      border: darken(0.1, colors.magenta),
    },
  },
  borders: {
    size: { thin: "1px", thick: "3px" },
    radius: {
      extraSmall: size_pix_to_rem[4],
      small: size_pix_to_rem[5],
      standard: size_pix_to_rem[6],
      medium: size_pix_to_rem[10],
      large: size_pix_to_rem[16],
      big: size_pix_to_rem[96],
      rounded: size_pix_to_rem[100],
    },
  },
  zIndex: {
    base: "auto",
    carouselArrow: 1,
    tooltip: 10,
    header: 20,
    backdrop: 30,
    overlay: 31,
    warning: 40,
    toast: 9999,
    background: -1,
  },
  opacity: {
    light: 0.75,
    medium: 0.5,
    high: 0.15,
  },
  breakpoints: {
    hypersmall: "360px",
    ultrasmall: "480px",
    xtrasmall: "640px",
    small: "768px",
    medium: "992px",
    large: "1200px",
    xtralarge: "1400px",
  },
  widgets: {
    colorPill: {
      shadow: "0px 12px 12px 0px rgba(0, 0, 0, 0.1)",
    },
    mixProducts: {
      height: size_pix_to_rem[172],
      results: {
        height: size_pix_to_rem[56],
      },
    },
    select: {
      minHeight: size_pix_to_rem[32],
    },
    modal: {
      maxHeight: "80%",
      small: {
        maxWidth: "62rem",
        width: "90%",
      },
      large: {
        maxWidth: "95%",
        width: "95%",
      },
    },
    baseData: {
      colors: {
        valueTrue: colors.green,
        valueFalse: colors.red,
        valueLabel: colors.grey,
      },
    },
    product: {
      colors: {
        state: {
          available: colors.green,
          unavailable: colors.red,
          unknown: colors.grey,
        },
      },
      sizes: {
        headerHeight: "3rem",
        variantsImage: {
          height: "3rem",
          width: "4.5rem",
        },
        youtube: {
          aspectRatio: "16 / 9",
          width: "900px",
        },
      },
    },
    h2: {
      fonts: {
        lineHeight: "2rem",
      },
    },
    paragraph: {
      fonts: {
        lineHeight: size_pix_to_rem[24],
      },
    },
    productCardLabel: {
      colors: {
        fg: colors.grey,
      },
    },
    productImageCard: {
      sizes: {
        maxHeight: "15rem",
      },
    },
    productSearch: {
      sizes: {
        searchIcon: size_pix_to_rem[36],
      },
    },
    reservationEdit: {
      spaces: {
        headerQuantityInput: "2.5rem",
      },
    },
    productStocks: {
      colors: {
        state: {
          unavailable: colors.red,
        },
      },
    },
    filter: {
      sizes: {
        minHeight: size_pix_to_rem[24],
      },
      menu: {
        maxHeight: "327px",
      },
    },
    button: {
      colors: {
        [variants.standard]: {
          bg: "transparent",
          border: "transparent",
          active: colors.darkBlue20,
        },
        [variants.primary]: {
          active: colors.darkBlue80,
        },
        [variants.secondary]: {
          active: colors.sky80,
        },
        [variants.ghost]: {
          active: colors.middleGrey,
        },
        [variants.disabled]: {
          secondary: colors.sky50,
        },
      },
      sizes: {
        minHeight: "5.25rem",
      },
      spaces: {
        marginLeft: "-2.75rem",
        clearButton: "-2rem",
      },
    },
    cart: {
      modal: {
        maxWidth: "498px",
      },
      priceModal: {
        maxWidth: "fit-content",
        maxHeight: "90%",
      },
      labelValueContainer: {
        minHeight: "56px",
      },
      confirmationModal: {
        maxWidth: "40%",
      },
      noteConfirmModal: {
        maxWidth: "600px",
      },
      disabledConfirmationModal: {
        maxWidth: "45%",
      },
      creditLimitModal: {
        maxWidth: "549px",
      },
      price: {
        minWidth: "60px",
      },
      position: {
        minWidth: "20px",
      },
      summary: {
        maxWidth: "337px",
        rightMargin: "2.3rem",
      },
      summaryInput: {
        maxWidth: "100px",
      },
      quantityInput: {
        width: "4.3rem",
      },
      quantityInputWide: {
        width: "10rem",
      },
    },
    checkBox: {
      colors: {
        bg: colors.lightGrey,
        active: colors.darkBlue,
        hover: darken(0.1, colors.darkBlue),
      },
      position: {
        standard: {
          iconTop: "-9px",
          iconLeft: "-2px",
        },
        small: {
          iconTop: "-8px",
          iconLeft: "-1px",
        },
      },
      size: {
        standard: {
          width: "18px",
          height: "18px",
        },
        small: {
          width: "16px",
          height: "16px",
        },
      },
      iconSize: "1.125rem",
    },
    radio: {
      position: {
        standard: {
          iconTop: "-9px",
          iconLeft: "0",
        },
        small: {
          iconTop: "-8px",
          iconLeft: "0",
        },
      },
      size: {
        standard: {
          width: "16px",
          height: "16px",
        },
        small: {
          width: "14px",
          height: "14px",
        },
      },
      iconSize: "1.125rem",
    },
    navigation: {
      sizes: {
        height: "100vh",
        maxWidth: "300px",
        icons: {
          normal: "2.25rem",
          small: "1.5rem",
        },
        logo: {
          big: {
            width: "123px",
            height: "50px",
          },
          small: {
            width: "64px",
            height: "26px",
          },
        },
        subNav: {
          minWidth: "180px",
          maxWidth: "250px",
        },
      },
    },
    campaignCard: {
      sizes: {
        height: "16.5rem",
      },
    },
    topArticles: {
      image: {
        sizes: {
          width: "2.5rem",
          height: "2.5rem",
        },
      },
    },
    divider: {
      size: "5px",
    },
    status: {
      size: "15px",
      borderRadius: "50%",
    },
  },
}

const borderWidth = theme.borders.size.thin
const borderStyle = "solid"

const generateBorder = (color) => ({
  width: borderWidth,
  color,
  style: borderStyle,
  inline: `${borderWidth} ${borderStyle} ${color}`,
})
// generate borders based on colors
Object.keys(variants).forEach((variant) => {
  theme.borders[variant] = generateBorder(theme.colors[variant].border)
})

export default theme
export { theme }
